"use client";

import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "../lib/utils";

/**
 * Typography — THE single text component. Every visible text node uses it.
 * Size/weight/family come from `variant`; color from `color`. Both resolve to
 * Trellis tokens (--yz-text-*, --yz-font-*) and scheme color roles — so there
 * are NEVER text color/style classes at call sites. Add a variant here when a
 * text look is missing; never hand-style text in a page.
 */
const typographyVariants = cva("", {
  variants: {
    variant: {
      display:
        "font-display text-[length:var(--yz-text-display)] leading-tight",
      h1: "font-display text-[length:var(--yz-text-h1)] leading-tight",
      h2: "font-display text-[length:var(--yz-text-h2)] leading-tight",
      h3: "font-display text-[length:var(--yz-text-h3)] leading-snug",
      h4: "font-display text-[length:var(--yz-text-h4)] leading-snug",
      h5: "font-display text-[length:var(--yz-text-h5)] leading-snug",
      /** Card-size title (e.g. ContentCard media-top). */
      h6: "font-display text-[length:var(--yz-text-h6)] leading-tight",
      /** Small display title (16px, display font) for tight spots, e.g. table column headers on mobile. */
      "title-sm":
        "font-display text-[length:var(--yz-text-body)] leading-tight",
      "body-lg":
        "font-body text-[length:var(--yz-text-body-lg)] leading-relaxed",
      body: "font-body text-[length:var(--yz-text-body)] leading-relaxed",
      "body-sm":
        "font-body text-[length:var(--yz-text-body-sm)] leading-normal",
      /** Form/section label. One line: medium weight, no leading. */
      label:
        "font-body text-[length:var(--yz-text-body-sm)] font-medium leading-none",
      "fine-print":
        "font-body text-[length:var(--yz-text-xs)] font-normal leading-relaxed",
      /** Small uppercase mono caption (field captions, eyebrows, card labels). */
      caption:
        "font-mono text-[length:var(--yz-text-11)] font-normal uppercase tracking-wide leading-none",
    },
    color: {
      primary: "text-scheme-text-primary",
      secondary: "text-scheme-text-secondary",
      muted: "text-scheme-text-muted",
      action: "text-scheme-action",
      error: "text-scheme-error",
      "on-action": "text-scheme-text-on-action",
      inverse: "text-scheme-text-on-inverse",
      inherit: "text-inherit",
    },
    weight: {
      normal: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
    },
  },
  defaultVariants: { variant: "body", color: "primary" },
});

type Tag =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "p"
  | "span"
  | "div"
  | "label";

export interface TypographyProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "color">,
    VariantProps<typeof typographyVariants> {
  as?: Tag;
}

export const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  ({ as = "p", variant, color, weight, className, ...props }, ref) => {
    const Comp = as as React.ElementType;
    return (
      <Comp
        ref={ref}
        className={cn(
          typographyVariants({ variant, color, weight }),
          className,
        )}
        {...props}
      />
    );
  },
);

Typography.displayName = "Typography";
